import React from "react";
import { X, Trash2, Send, Heart } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { useWishlist } from "@/context/WishlistContext";
import { useRates } from "@/context/RatesContext";
import { computePrice, fmtINR } from "@/lib/pricing";
import { api, fileUrl, formatError } from "@/lib/api";

export default function WishlistDrawer() {
  const { items, remove, clear, open, setOpen } = useWishlist();
  const { rates, settings, gstOn } = useRates();
  const [name, setName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const priced = items.map((it) => {
    const p = computePrice(it, rates, gstOn);
    return { ...it, _price: p.final };
  });
  const total = priced.reduce((s, it) => s + it._price, 0);

  const buildMessage = () => {
    const lines = priced.map((it, i) => {
      const specs = [];
      if (it.gold_weight > 0) specs.push(`Gold: ${it.gold_weight}g`);
      if (it.diamond_weight > 0) specs.push(`Diamond: ${it.diamond_weight}ct`);
      specs.push(it.karat);
      const spec = specs.filter(Boolean).join(" | ");
      return `${i + 1}. ${it.item_name} (${it.tag_no})\n   ${spec} | ${fmtINR(it._price)}`;
    });
    return `Hello Bholaa Jewels ✦\n\nI'm interested in the following items from your catalogue:\n\n${lines.join("\n\n")}\n\n💰 Total Estimated Value: ${fmtINR(total)}\n${gstOn ? "(incl. GST)" : "(excl. GST)"}\n\n👤 Name: ${name}\n📞 Phone: ${phone}\n\nKindly contact me at your convenience.\nThank you!`;
  };

  const sendEnquiry = async () => {
    if (!name.trim() || !phone.trim()) {
      toast.error("Please enter your name and phone number");
      return;
    }
    if (items.length === 0) {
      toast.error("Your wishlist is empty");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", {
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        items: priced.map((it) => ({ tag_no: it.tag_no, item_name: it.item_name, price: it._price })),
        total_estimated: total,
        gst_included: gstOn,
      });
      const waNum = (settings?.whatsapp_number || "917814854580").replace(/\D/g, "");
      const text = encodeURIComponent(buildMessage());
      const url = waNum ? `https://wa.me/${waNum}?text=${text}` : `https://wa.me/?text=${text}`;
      window.open(url, "_blank");
      toast.success("Enquiry sent! Opening WhatsApp…");
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Failed to send enquiry");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating pill */}
      {items.length > 0 && !open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-8 left-8 z-[60] flex items-center gap-2 bg-rose-gold text-ink-900 px-5 py-3 shadow-xl hover:bg-champagne transition-colors font-medium tracking-wide"
          data-testid="wishlist-floating-pill"
        >
          <Heart className="w-4 h-4 fill-ink-900" />
          My Wishlist ({items.length})
        </button>
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="dark-surface bg-ink-800/95 border-l border-rose-gold/20 text-ivory w-full sm:max-w-md overflow-y-auto p-0">
          <SheetHeader className="p-6 border-b border-white/5">
            <SheetTitle className="font-display text-2xl text-ivory flex items-center gap-2">
              <Heart className="w-5 h-5 text-rose-gold fill-rose-gold" /> My Wishlist
            </SheetTitle>
            <p className="text-sm text-ivory/60">{items.length} item{items.length !== 1 ? "s" : ""} saved</p>
          </SheetHeader>

          <div className="p-6 space-y-3" data-testid="wishlist-items">
            {items.length === 0 ? (
              <div className="py-16 text-center text-ivory/40">
                <Heart className="w-10 h-10 mx-auto mb-4 text-rose-gold/30" />
                <p>Your wishlist is empty.</p>
                <p className="text-xs mt-2">Tap the heart on any piece to save it here.</p>
              </div>
            ) : (
              priced.map((it) => (
                <div key={it.tag_no} className="flex gap-3 p-3 bg-ink-900/50 border border-white/5" data-testid={`wishlist-item-${it.tag_no}`}>
                  <div className="w-16 h-16 bg-ink-700 flex-shrink-0 overflow-hidden">
                    {it.image_path ? (
                      <img src={fileUrl(it.image_path)} alt={it.item_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-rose-gold/20 text-xs">—</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="overline text-rose-gold">{it.tag_no}</div>
                    <div className="font-display text-sm text-ivory truncate">{it.item_name}</div>
                    <div className="text-champagne text-sm font-medium mt-1">{fmtINR(it._price)}</div>
                  </div>
                  <button onClick={() => remove(it.tag_no)} className="p-2 text-ivory/40 hover:text-hallmark" data-testid={`wishlist-remove-${it.tag_no}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>

          {items.length > 0 && (
            <div className="p-6 border-t border-white/5 space-y-4 sticky bottom-0 bg-ink-800/95 backdrop-blur">
              <div className="flex justify-between items-baseline">
                <span className="overline text-ivory/60">Estimated Total {gstOn ? "(incl. GST)" : "(excl. GST)"}</span>
                <span className="font-display text-2xl text-champagne" data-testid="wishlist-total">{fmtINR(total)}</span>
              </div>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full bg-ink-900 border border-white/10 px-4 py-3 text-sm text-ivory placeholder-ivory/30 focus:border-rose-gold"
                data-testid="enquiry-name-input"
              />
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone number"
                type="tel"
                className="w-full bg-ink-900 border border-white/10 px-4 py-3 text-sm text-ivory placeholder-ivory/30 focus:border-rose-gold"
                data-testid="enquiry-phone-input"
              />
              <button
                onClick={sendEnquiry}
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 bg-rose-gold text-ink-900 py-4 font-medium tracking-widest uppercase text-sm hover:bg-champagne transition-colors disabled:opacity-50"
                data-testid="send-enquiry-button"
              >
                <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Send Enquiry via WhatsApp"}
              </button>
              <button onClick={clear} className="w-full text-xs text-ivory/40 hover:text-hallmark tracking-widest uppercase" data-testid="clear-wishlist-button">Clear wishlist</button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
